/**
 * Interactive seat map renderer.
 * Props:
 *   - map: serialized seat map { mode, width, height, stage, zones[] }
 *   - mode: "viewer" | "editor"
 *   - selectedSeats: array of seat ids selected (for viewer/editor)
 *   - onSeatToggle(seatId, zoneId): callback when a seat is clicked
 *   - onZoneClick(zoneId): callback when a zone is clicked (viewer zones-mode)
 *   - selectedZoneId: zone id currently selected (editor)
 *   - getCatColor(categoryId): override color from category
 *   - maxSelectable: cap on seat selection (viewer)
 */
import React from "react";

const STAGE_COLOR = "#222";

export default function SeatMapView({
  map,
  mode = "viewer",
  selectedSeats = [],
  onSeatToggle,
  onZoneClick,
  selectedZoneId,
  highlightCategoryId,
  maxSelectable,
}) {
  if (!map) return null;
  const { width, height, stage, zones = [] } = map;
  const selectedSet = new Set(selectedSeats);

  return (
    <div style={{ background: "#f7f7f8", borderRadius: 12, padding: 10, overflow: "auto", maxWidth: "100%" }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={{ width: "100%", maxWidth: width, height: "auto", display: "block", background: "white", borderRadius: 8 }}
        data-testid="seatmap-svg"
      >
        {/* Stage */}
        {stage && (
          <g data-testid="seatmap-stage">
            <rect x={stage.x} y={stage.y} width={stage.width} height={stage.height}
              fill={STAGE_COLOR} rx={6} ry={6} />
            <text x={stage.x + stage.width / 2} y={stage.y + stage.height / 2 + 5}
              textAnchor="middle" fontSize="14" fontWeight="700" fill="white" letterSpacing="2">
              {stage.label || "SCÈNE"}
            </text>
          </g>
        )}

        {/* Zones */}
        {zones.map((z) => {
          const isHighlighted = highlightCategoryId === z.category_id;
          const isSelected = selectedZoneId === z.id;
          const isDimmed = highlightCategoryId && !isHighlighted;
          const shape = z.shape || {};
          const fillOpacity = isDimmed ? 0.15 : (isHighlighted ? 0.35 : 0.22);
          return (
            <g key={z.id} data-testid={`seatmap-zone-${z.id}`} style={{ cursor: onZoneClick ? "pointer" : "default" }}
              onClick={(e) => {
                if (mode === "viewer" && (z.seats?.length || 0) === 0 && onZoneClick) {
                  onZoneClick(z.id);
                } else if (mode === "editor" && onZoneClick) {
                  onZoneClick(z.id);
                  e.stopPropagation();
                }
              }}>
              <rect
                x={shape.x || 0}
                y={shape.y || 0}
                width={shape.width || 50}
                height={shape.height || 50}
                fill={z.color || "#01696F"}
                fillOpacity={fillOpacity}
                stroke={isSelected ? "#DA7101" : (z.color || "#01696F")}
                strokeWidth={isSelected ? 3 : 1.5}
                rx={8} ry={8}
              />
              <text
                x={(shape.x || 0) + (shape.width || 50) / 2}
                y={(shape.y || 0) + 16}
                textAnchor="middle"
                fontSize="12"
                fontWeight="700"
                fill={z.color || "#01696F"}
                pointerEvents="none"
              >
                {z.name}
              </text>

              {/* Seats inside zone */}
              {(z.seats || []).map((s) => {
                const isSold = s.sold;
                const isSel = selectedSet.has(s.id);
                const wrongCat = highlightCategoryId && z.category_id !== highlightCategoryId;
                const interactive = mode !== "viewer" || !isSold;
                const disabledForSelection = mode === "viewer" && wrongCat;
                let fill = "#fff";
                let strokeColor = z.color || "#01696F";
                if (isSold) { fill = "#bbb"; strokeColor = "#aaa"; }
                else if (isSel) { fill = "#DA7101"; strokeColor = "#DA7101"; }
                else if (wrongCat) { fill = "#f4f4f4"; strokeColor = "#ddd"; }
                else { fill = z.color || "#01696F"; }
                return (
                  <circle
                    key={s.id}
                    cx={s.x}
                    cy={s.y}
                    r={9}
                    fill={fill}
                    fillOpacity={isSold ? 0.7 : (isSel ? 1 : (wrongCat ? 0.3 : 0.6))}
                    stroke={strokeColor}
                    strokeWidth={isSel ? 2 : 1}
                    style={{ cursor: interactive && !disabledForSelection ? "pointer" : "not-allowed" }}
                    data-testid={`seatmap-seat-${s.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isSold || disabledForSelection) return;
                      if (!isSel && maxSelectable && selectedSeats.length >= maxSelectable) return;
                      if (onSeatToggle) onSeatToggle(s.id, z.id);
                    }}
                  >
                    <title>{s.label}{isSold ? " — vendu" : ""}</title>
                  </circle>
                );
              })}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
